//notificationmodal.js
import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import "../styles/Notification.css";
import { IoMdClose } from "react-icons/io";

export default function NotificationModal({
  notifications,
  onClose,
  onClear,
  isClearing = false,
  companyTimeZone = "UTC",
  mode = "tabs", // "tabs" | "activity" | "announcements"
  title = "Notifications",

  // ✅ NEW (optional override)
  subtitle = "",
}) {
  const [tab, setTab] = useState(
    mode === "announcements" ? "announcements" : "activity"
  );

  useEffect(() => {
    if (mode === "activity") setTab("activity");
    if (mode === "announcements") setTab("announcements");
  }, [mode]);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // --- helpers ---
  const startCase = (s = "") =>
    s
      .replace(/[_-]/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^./, (c) => c.toUpperCase());

  const fmtDate = (iso, tz) =>
    iso ? DateTime.fromISO(iso, { zone: tz }).toFormat("MM/dd/yyyy") : null;

  const fmtRange = (fromIso, toIso, tz) => {
    const a = fmtDate(fromIso, tz);
    const b = fmtDate(toIso, tz);
    if (!a && !b) return "";
    if (a && !b) return a;
    if (!a && b) return b;
    return a === b ? a : `${a} – ${b}`;
  };
  const getSubtitle = () => {
    // manual override wins
    if (subtitle && subtitle.trim()) return subtitle;

    // auto by mode/tab
    if (mode === "activity") {
      return "Company alerts and activity updates.";
    }

    if (mode === "announcements") {
      return "Updates, new features and important platform announcements.";
    }

    // mode === "tabs"
    if (tab === "activity") {
      return "Company alerts and activity updates.";
    }

    return "Updates, new features and important platform announcements.";
  };

  const subtitleText = getSubtitle();

  // NEW: format minutes into a friendly "X hrs Y mins"
  const formatMinutesAsDuration = (mins) => {
    if (mins == null || isNaN(mins)) return null;
    const total = Math.round(Number(mins));
    const h = Math.floor(total / 60);
    const m = total % 60;

    if (h <= 0) {
      return `${total} min${total === 1 ? "" : "s"}`;
    }
    if (m === 0) {
      return `${h} hr${h === 1 ? "" : "s"}`;
    }
    return `${h} hr${h === 1 ? "" : "s"} ${m} min${m === 1 ? "" : "s"}`;
  };

  // --- message templates by type ---
  const formatNotification = (n, tz) => {
    const title = startCase(n.type || "Notification");
    switch (n.type) {
      case "platformUpdates":
        return {
          title: n.title || "Announcement",
          body: n.message || "",
          url: n.url || null,
          badge: (n.severity || "info").toLowerCase(), // info | low | high
        };

      case "ticketSubmitted": {
        const who = n.employeeName || "An employee";
        const p = (n.priority || "").toLowerCase();
        const cat = n.category || "ticket";
        const subject = n.title ? `: “${n.title}”` : "";
        return {
          title: "Ticket Submitted",
          body: `${who} submitted a ${p || "low"} ${cat} ticket${subject}.`,
        };
      }

      case "taskSubmitted": {
        const who = n.employeeName || "An employee";
        const taskTitle = n.taskTitle ? `: “${n.taskTitle}”` : "";
        const site = n.workSiteName ? ` • ${n.workSiteName}` : "";
        return {
          title: "Task Submitted",
          body: `${who} submitted a task for review${taskTitle}${site}.`,
        };
      }

      case "leaveRequest": {
        const who = n.employeeName || "An employee";
        const leave = n.leaveType || "leave";
        const range =
          fmtRange(n.startDate, n.endDate, tz) ||
          (n.message || "").replace(/^Leave request submitted by.*for\s*/i, "");
        const status = n.status ? ` • ${startCase(n.status)}` : "";
        return {
          title,
          body: `${who} requested ${leave}${
            range ? ` — ${range}` : ""
          }${status}.`,
        };
      }

      case "lateClockIn": {
        const who = n.employeeName || "An employee";

        const lateLabel =
          typeof n.minutesLate === "number"
            ? formatMinutesAsDuration(n.minutesLate)
            : null;

        const at = n.clockIn
          ? DateTime.fromISO(n.clockIn, { zone: tz }).toFormat("h:mm a")
          : null;

        return {
          title: "Late clock-in",
          body: `${who} clocked in${
            lateLabel ? ` ${lateLabel} late` : " late"
          }${at ? ` (${at})` : ""}.`,
        };
      }

      case "overtime": {
        const who = n.employeeName || "An employee";
        const hours = typeof n.hours === "number" ? n.hours.toFixed(2) : null;
        return {
          title: "Overtime reached",
          body: `${who} exceeded daily hours${hours ? ` — ${hours}h` : ""}.`,
        };
      }

      default:
        return { title, body: n.message || "" };
    }
  };

  // --- split into tabs ---
  const isAnnouncement = (n) =>
    (n.type || "").toLowerCase() === "platformupdates";

  const activity = useMemo(
    () => notifications.filter((n) => !isAnnouncement(n)),
    [notifications]
  );

  const announcements = useMemo(() => notifications.filter(isAnnouncement), [
    notifications,
  ]);

  const shown = useMemo(() => {
    if (mode === "activity") return activity;
    if (mode === "announcements") return announcements;
    // default: tabs mode
    return tab === "activity" ? activity : announcements;
  }, [mode, tab, activity, announcements]);

  return (
    <div
      className="notification-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="notif-title"
    >
      <aside className="notification-modal">
        {/* Header: title + close only */}
        <div className="notification-header">
          <div className="notif-header-text">
            <h2 id="notif-title">{title}</h2>

            {/* ✅ NEW subtitle line */}
            {subtitleText && <p className="notif-subtitle">{subtitleText}</p>}
          </div>

          <button
            className="close-btn"
            onClick={onClose}
            aria-label="Close notifications"
          >
            <IoMdClose className="closebtn-icon" />
          </button>
        </div>

        {/* Toolbar: tabs left, clear right */}
        <div className="notif-toolbar">
          {mode === "tabs" && (
            <div className="tabs">
              <button
                className={`tab-btn ${tab === "activity" ? "active" : ""}`}
                onClick={() => setTab("activity")}
              >
                Activity
                {!!activity.length && (
                  <span className="tab-count">{activity.length}</span>
                )}
              </button>

              <button
                className={`tab-btn ${tab === "announcements" ? "active" : ""}`}
                onClick={() => setTab("announcements")}
              >
                Announcements
                {!!announcements.length && (
                  <span className="tab-count">{announcements.length}</span>
                )}
              </button>
            </div>
          )}

          <button
            className="pill-btn ghost danger"
            onClick={() => onClear?.(shown.map((n) => n.id))}
            disabled={isClearing || shown.length === 0}
            aria-label="Clear shown notifications"
            title="Archive the notifications in this tab"
          >
            {isClearing ? "Clearing…" : "Clear shown"}
          </button>
        </div>

        {/* List */}
        {shown.length === 0 ? (
          <p className="no-notifications">No notifications in this tab.</p>
        ) : (
          <div className="notification-list">
            {shown
              .slice()
              .sort((a, b) =>
                (a.timestamp?.toDate?.() || new Date(a.timestamp)) <
                (b.timestamp?.toDate?.() || new Date(b.timestamp))
                  ? 1
                  : -1
              )
              .map((n, i) => {
                const ts = n.timestamp?.toDate?.() || new Date(n.timestamp);
                const when = DateTime.fromJSDate(ts, {
                  zone: companyTimeZone,
                }).toFormat("MM/dd/yyyy • hh:mm a");

                const isAnnc = isAnnouncement(n);
                const { title, body, url, badge } = formatNotification(
                  n,
                  companyTimeZone
                );

                // add styling hooks for kind + severity
                const cardClass = `notification-card ${
                  isAnnc ? "kind-annc" : "kind-activity"
                } ${badge ? `sev-${badge}` : ""}`;

                return (
                  <div key={n.id || i} className={cardClass}>
                    <div className="notification-type">
                      {title}
                      {badge && (
                        <span className={`notif-badge sev-${badge}`}>
                          {startCase(badge)}
                        </span>
                      )}
                    </div>

                    <div className="notification-message">
                      {body}
                      {url && (
                        <>
                          {" · "}
                          <a href={url} target="_blank" rel="noreferrer">
                            Learn more
                          </a>
                        </>
                      )}
                    </div>

                    <div className="notification-timestamp">{when}</div>
                  </div>
                );
              })}
          </div>
        )}
      </aside>
    </div>
  );
}
